import React, { useEffect, useState } from "react";
import { Ticket } from "../../interface/ticket";
import { respondTicketService } from "../../services/ticketService";
import { webSocketURL } from "../../services/endpointUrl";

export default function ChatBox({
  ticket,
  onClose,
}: {
  ticket: Ticket;
  onClose: () => void;
}) {
  const [description, setDescription] = useState("");
  const [formIsComplete, setFormIsComplete] = useState(false); 
  const [conversation, setConversation] = useState(ticket.conversation ?? []); // 👈 local conversation state

  const checkForm = () => {
    setFormIsComplete(description.trim() !== "");
  };

  useEffect(() => {
    async function connectTMS() {
      try {
        // Create WebSocket connection
        const ws = new WebSocket(webSocketURL);

        ws.onopen = () => {
          console.log("✅ Connected to WebSocket server");
          ws.send(ticket.ticket_id ?? "");
        };

        ws.onmessage = (event) => {
          try {
            const ack = JSON.parse(event.data);
            console.log("📩 WS message:", ack);

            // 👇 Add new message to conversation
            if(ack.user == "system"){
              setConversation((prev) => [
                ...prev,
                {
                  user: ack.user ?? "system", // adjust if your payload differs
                  message: ack.message ?? "",
                  time: ack.time ?? new Date().toLocaleTimeString(),
                },
              ]);
            }
          } catch (e) {
            console.error("Invalid WS data:", event.data);
          }
        };
      } catch (err) {
        console.error("Error connecting to WS:", err);
      }
    }

    connectTMS();
  }, [ticket.ticket_id]);

  const sendMessage = async () => {
    const data = {
      ticket_id: ticket.ticket_id,
      description: description,
    };
    const response = await respondTicketService(data);
    console.log(response);

    if (response.success) {
      setConversation((prev) => [
        ...prev,
        {
          user: ticket.customer_name, // adjust if your payload differs
          message: description ?? "",
          time: new Date().toLocaleTimeString(),
        },
      ]);
      setDescription("");
      setFormIsComplete(false);
    }
  };
  
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex justify-between items-center border-b pb-2 mb-2">
        <h2 className="text-lg font-semibold text-gray-900">
          Chat: {ticket.more_info?.[0]?.description}
        </h2>
        <button
          onClick={onClose}
          className="text-sm text-blue-600 hover:underline"
        >
          Back
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 px-2">
        {conversation.map((msg, idx) => {
          const isUser = msg.user === "system"; // 👈 adjust as per your payload
          return (
            <div
              key={idx}
              className={`flex ${isUser ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-xs px-4 py-2 rounded-2xl text-sm shadow ${
                  isUser
                    ? "bg-blue-600 text-white rounded-br-none"
                    : "bg-gray-200 text-gray-900 rounded-bl-none"
                }`}
              >
                {msg.type !="image" && (
                  <p>{msg.message}</p>
                )}
                {msg.type =="image" && (
                  <p><img src={msg.message}></img></p>
                )}
                <span className="block mt-1 text-xs opacity-70">
                  {msg.time}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Input */}
      <div className="mt-3 flex border-t pt-2">
        <input
          type="text"
          placeholder="Type a reply..."
          className="flex-1 border rounded-l px-3 py-2 text-sm"
          value={description}
          onKeyUp={checkForm}
          onChange={(e) => setDescription(e.target.value)}
        />
        <button 
          className={`rounded px-4 py-2 text-white ${
            formIsComplete
              ? "bg-blue-600 hover:bg-blue-700"
              : "bg-gray-400 cursor-not-allowed"
          }`}
          disabled={!formIsComplete}
          onClick={sendMessage}
        >
          Send
        </button>
      </div>
    </div>
  );
}
